'use client';

import type React from 'react';
import { createContext, useContext, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import useGetImageUrlsByTags from '@/components/hooks/use-get-image-urls-by-tags';

const MAX_RESULTS = 1;

interface InitialState {
  url: string | null;
  isLoading: boolean;
  error: string | null;
}

const INITIAL_STATE: InitialState = {
  error: null,
  isLoading: false,
  url: null,
};

export const HeroImageContext = createContext(INITIAL_STATE);

export function useHeroImageContext() {
  return useContext(HeroImageContext);
}

interface HeroImageProviderProperties {
  children: React.ReactNode;
}

// NOTE: HeroImageProvider uses the current path to get the hero
// image from Cloudinary by tags. Ex: hero_request_consultation
//
// This way, you can simply change the tag the change the hero image
function HeroImageProvider({ children }: HeroImageProviderProperties) {
  const pathname = usePathname();

  const tags = useMemo(
    () => `hero${pathname}`.replaceAll(/[\s/-]|(?:%20)/g, '_'),
    [pathname],
  );

  const { error, isLoading, data } = useGetImageUrlsByTags({
    maxResults: MAX_RESULTS,
    tags,
  });

  const context = useMemo(
    () => ({
      error: error?.message ?? null,
      isLoading,
      url: data && data.length > 0 ? data[0].public_id : null,
    }),
    [error, isLoading, data],
  );

  return (
    <HeroImageContext.Provider value={context}>
      {children}
    </HeroImageContext.Provider>
  );
}

export default HeroImageProvider;
